import { Copy, ExternalLink, Mail, type LucideIcon } from 'lucide-react';
import type { RelatedItem } from '../../data/queries';
import type { DecisionKind, Employee, EmployeeSummary, Item, ItemEvent } from '../../domain/models';
import { editedByPerson, eventComment, eventNote, isWorking, publishesOnApproval } from '../../domain/models';
import { contactDisplay, manualTarget } from '../../domain/payload';
import type { Translate } from '../../l10n/i18n';
import type { Locale } from '../../l10n/messages';
import { nameDative } from '../core/presentation';

// What the item screen decides from the item alone, ported from the mobile app's item_screen.dart and
// item_view_model.dart. What the person may do always comes from `item.actions`.

/** Employees of the item's workspace who can take its work: not the one who made it, and not one
 * already working on it. */
export function handoffTargets(item: Item, employees: Employee[], related: RelatedItem[]): Employee[] {
  const busy = new Set(related.filter((r) => isWorking(r.item)).map((r) => r.item.employee.id));
  return employees.filter(
    (e) =>
      e.takesTasks && e.active && e.workspaceId === item.workspace.id && e.id !== item.employee.id && !busy.has(e.id),
  );
}

/** A colleague by name, preferring this item's workspace: who asks for access, for example. */
export function employeeNamed(employees: Employee[], workspaceId: string, name: string): EmployeeSummary | undefined {
  const named = employees.filter((e) => e.name === name);
  const e = named.find((x) => x.workspaceId === workspaceId) ?? named[0];
  return e && { id: e.id, role: e.role, name: e.name, title: e.title, avatar: e.avatar, color: e.color };
}

/** The person's last "request changes" comment, shown above the revision that answers it. */
export function lastChangeRequest(events: ItemEvent[]): string | undefined {
  const e = events.findLast((x) => x.kind === 'changes_requested');
  return e && eventComment(e);
}

/** What the employee said it changed in the current revision. */
export function revisionNote(events: ItemEvent[]): string | undefined {
  const e = events.findLast((x) => x.kind === 'revised' && !editedByPerson(x));
  return e && eventNote(e);
}

/** How a person carries out approved work by hand: the button that copies the text and opens where it
 * goes, and the one that reports it done. */
export interface ManualPlan {
  open: string;
  icon: LucideIcon;
  target?: string;
  done: string;
  message: boolean;
}

export function manualPlan(t: Translate, item: Item): ManualPlan {
  const target = manualTarget(item.payload);
  const message = item.payload.type === 'message_reply';
  const mail = target?.startsWith('mailto:') ?? false;
  let open: string;
  if (target === undefined) open = t('actionCopy');
  else if (mail) open = t('actionWriteEmail');
  else if (item.kind === 'group_post') open = t('actionOpenGroup');
  else open = t('actionOpen');
  return {
    open,
    icon: target === undefined ? Copy : mail ? Mail : ExternalLink,
    target,
    done: message ? t('actionSent') : t('actionPublished'),
    message,
  };
}

/** What the person does now, step by step, for work they carry out by hand. */
export function manualSteps(t: Translate, item: Item, plan: ManualPlan): string[] {
  const target = plan.target;
  const mail = target?.startsWith('mailto:') ?? false;
  const headline = item.payload.type === 'social_post' && (item.payload.headline?.length ?? 0) > 0;
  const links = item.links.length > 0;
  let first: string;
  if (target === undefined) first = t('stepCopy', { button: plan.open });
  else if (mail) first = t('stepOpenMail', { button: plan.open });
  else first = t('stepOpenCopies', { button: plan.open });
  let second: string;
  if (mail) second = t('stepCheckSend');
  else if (target === undefined && links && plan.message) second = t('stepLinkPasteSend');
  else if (target === undefined && links) second = t('stepLinkPastePublish');
  else if (headline) second = t('stepPasteHeadline');
  else if (plan.message) second = t('stepPasteSend');
  else second = t('stepPastePublish');
  return [first, second, t('stepMarkDone', { button: plan.done })];
}

/** Who a document goes to (an invoice's buyer), or nothing for one that is published (a product
 * description, an article). */
export function documentAddressee(item: Item): string | undefined {
  const p = item.payload;
  if (p.type !== 'document') return undefined;
  const who = contactDisplay(p.for) || p.for.email || '';
  return who.length > 0 ? who : undefined;
}

/** The approve button's words: what approving does for this item. */
export function approveLabel(t: Translate, item: Item): string {
  if (item.kind === 'access_request') return t('actionAllowUse');
  if (item.kind === 'deliverable') return t('actionSendToClient');
  if (publishesOnApproval(item)) return t('actionPublish');
  // An ad change is not sent to anyone; approving it changes the campaign (the dialog says so).
  if (item.kind === 'ad') return t('actionApprove');
  // A document is sent when it has an addressee (an invoice), published when it has none.
  if (item.kind === 'document' && item.outward) return documentAddressee(item) ? t('actionSend') : t('actionPublish');
  return item.outward ? t('actionSend') : t('actionApprove');
}

/** Who asked for an access request, for the sentences around it. */
export const requester = (item: Item) =>
  item.payload.type === 'access_request' && item.payload.requestedBy ? item.payload.requestedBy : item.employee.name;

/** Who receives outward work, for the confirmation: the client or the addressee. */
export function recipient(t: Translate, item: Item): string {
  const p = item.payload;
  const who =
    p.type === 'offer' || p.type === 'deliverable'
      ? contactDisplay(p.client)
      : p.type === 'message_reply'
        ? contactDisplay(p.to)
        : (documentAddressee(item) ?? '');
  return who.length > 0 ? who : t('confirmSendClientFallback');
}

/** The resource an access request asks for, for the confirmation. */
export const accessResource = (item: Item) => (item.payload.type === 'access_request' ? item.payload.resource : item.title);

/** What to say once a decision went through. */
export function decidedMessage(t: Translate, locale: Locale, item: Item, kind: DecisionKind): string {
  const name = item.employee.name;
  if (item.type === 'screening') return kind === 'approve' ? t('decidedLetIn', { name }) : t('decidedSpam');
  if (item.type === 'report') return t('decidedSeen');
  switch (kind) {
    case 'approve': {
      const p = item.payload;
      if (p.type === 'access_request') {
        return p.validMinutes !== undefined
          ? t('decidedAccess', { name: requester(item), minutes: p.validMinutes })
          : t('decidedAccessOnce', { name: requester(item) });
      }
      // An ad change changes a campaign and a document without an addressee is published: neither is sent.
      const sent = item.kind === 'document' ? documentAddressee(item) !== undefined : item.kind !== 'ad';
      return item.outward && sent ? t('decidedSent') : t('decidedApproved');
    }
    case 'reject':
      return t('decidedRejected');
    case 'request_changes':
      return t('decidedChanges', { name: nameDative(locale, name) });
  }
}
